import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  XMarkIcon,
  CalendarIcon,
  UserIcon,
  FlagIcon,
  ChatBubbleLeftIcon,
  RocketLaunchIcon,
  Squares2X2Icon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { format, formatDistanceToNow } from 'date-fns';
import axios from 'axios';
import { MasterPlanTask } from '../services/masterPlan';
import { getAuthHeaders } from '../utils/auth';
import { MarkdownEditor, MarkdownViewer } from './markdown';
import { formatTaskStatus } from '../utils/enumFormatting';

import { API_URL } from '../constants/urls';

interface TaskComment {
  id: string;
  task_id: string;
  user_id: string;
  user: {
    id: string;
    email: string;
    first_name: string | null;
    last_name: string | null;
    full_name: string | null;
  } | null;
  content: string;
  created_at: string;
  updated_at: string;
}

interface TaskDetailPanelProps {
  task: MasterPlanTask | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (task: MasterPlanTask) => void;
  onDelete: (taskId: string) => void;
  organizationId: string;
  currentUserId: string;
  canEdit: boolean;
  isAdmin: boolean;
  segments?: any[];
  propositions?: any[];
  groups?: any[];
  positions?: any[];
  companies?: any[];
}

const TaskDetailPanel: React.FC<TaskDetailPanelProps> = ({
  task,
  isOpen,
  onClose,
  onUpdate,
  onDelete,
  organizationId,
  currentUserId,
  canEdit,
  isAdmin,
  segments = [],
  propositions = [],
  groups = [],
  positions = [],
  companies = [],
}) => {
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState<Partial<MasterPlanTask>>({});
  const [saving, setSaving] = useState(false);
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [savingComment, setSavingComment] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentContent, setEditingCommentContent] = useState('');
  const [keyResults, setKeyResults] = useState<any[]>([]);

  // Fetch data needed for dropdowns
  const [organizationMembers, setOrganizationMembers] = useState<any[]>([]);
  const [areas, setAreas] = useState<any[]>([]);
  const [okrs, setOkrs] = useState<any[]>([]);

  // Load dropdown data when panel opens
  useEffect(() => {
    if (!isOpen || !organizationId) return;

    const loadDropdownData = async () => {
      try {
        // Fetch organization members
        const membersResponse = await axios.get(
          `${API_URL}/organizations/${organizationId}/members`,
          { headers: getAuthHeaders() },
        );
        setOrganizationMembers(membersResponse.data || []);

        // Fetch areas
        const areasResponse = await axios.get(
          `${API_URL}/areas?organization_id=${organizationId}`,
          { headers: getAuthHeaders() },
        );
        setAreas(areasResponse.data || []);

        // Fetch OKRs (active and draft only)
        const okrsResponse = await axios.get(
          `${API_URL}/okrs?organization_id=${organizationId}`,
          { headers: getAuthHeaders() },
        );
        const allOkrs = okrsResponse.data || [];

        // Filter to active/draft OKRs for selection
        const selectableOkrs = allOkrs.filter(
          (okr: any) => okr.status === 'active' || okr.status === 'draft',
        );

        // If task has a completed/cancelled OKR, include it as disabled option
        if (
          task?.okr_id &&
          !selectableOkrs.find((okr: any) => okr.id === task.okr_id)
        ) {
          const currentOkr = allOkrs.find((okr: any) => okr.id === task.okr_id);
          if (currentOkr) {
            selectableOkrs.push({
              ...currentOkr,
              disabled: true,
              displaySuffix: ` (${currentOkr.status === 'completed' ? 'Completed' : 'Cancelled'})`,
            });
          }
        }

        setOkrs(selectableOkrs);
      } catch (error) {
        console.error('Failed to load dropdown data:', error);
      }
    };

    loadDropdownData();
  }, [isOpen, organizationId]);

  useEffect(() => {
    if (task) {
      setEditForm({
        title: task.title,
        description: task.description || '',
        notes: task.notes || '',
        status: task.status,
        priority: task.priority,
        due_date: task.due_date,
        assigned_to_id: task.assigned_to_id,
        area_id: task.area_id,
        okr_id: task.okr_id,
        key_result_id: task.key_result_id,
      });
      loadComments();
      if (task.okr_id) {
        loadKeyResults(task.okr_id);
      }
    }
  }, [task]);

  const loadComments = async () => {
    if (!task) return;

    setLoadingComments(true);
    try {
      const response = await axios.get(
        `${API_URL}/master-plan/tasks/${task.id}/comments`,
        {
          params: { organization_id: organizationId },
          headers: getAuthHeaders(),
        },
      );
      setComments(response.data);
    } catch (error) {
      console.error('Failed to load comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };

  const loadKeyResults = async (okrId: string) => {
    try {
      // Key Results are included in the OKR object
      const response = await axios.get(`${API_URL}/okrs/${okrId}`, {
        params: { organization_id: organizationId },
        headers: getAuthHeaders(),
      });
      setKeyResults(response.data.key_results || []);
    } catch (error) {
      console.error('Failed to load key results:', error);
      setKeyResults([]);
    }
  };

  const handleSave = async () => {
    if (!task) return;

    setSaving(true);
    try {
      // Prepare update data with explicit null handling for OKR fields
      const updateData: any = { ...editForm };

      // Explicitly set to null when clearing OKR alignment (same as TaskModal)
      if (!editForm.okr_id) {
        updateData.okr_id = null;
        updateData.key_result_id = null;
      }

      const response = await axios.put(
        `${API_URL}/master-plan/tasks/${task.id}`,
        updateData,
        {
          params: { organization_id: organizationId },
          headers: getAuthHeaders(),
        },
      );
      onUpdate(response.data);
      setEditMode(false);
    } catch (error) {
      console.error('Failed to update task:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleAddComment = async () => {
    if (!task || !newComment.trim()) return;

    setSavingComment(true);
    try {
      const response = await axios.post(
        `${API_URL}/master-plan/tasks/${task.id}/comments`,
        { content: newComment },
        {
          params: { organization_id: organizationId },
          headers: getAuthHeaders(),
        },
      );
      setComments([response.data, ...comments]);
      setNewComment('');
    } catch (error) {
      console.error('Failed to add comment:', error);
    } finally {
      setSavingComment(false);
    }
  };

  const handleEditComment = async (commentId: string) => {
    if (!task || !editingCommentContent.trim()) return;

    try {
      const response = await axios.put(
        `${API_URL}/master-plan/tasks/${task.id}/comments/${commentId}`,
        { content: editingCommentContent },
        {
          params: { organization_id: organizationId },
          headers: getAuthHeaders(),
        },
      );
      setComments(
        comments.map((c) => (c.id === commentId ? response.data : c)),
      );
      setEditingCommentId(null);
      setEditingCommentContent('');
    } catch (error) {
      console.error('Failed to update comment:', error);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!task) return;

    try {
      await axios.delete(
        `${API_URL}/master-plan/tasks/${task.id}/comments/${commentId}`,
        {
          params: { organization_id: organizationId },
          headers: getAuthHeaders(),
        },
      );
      setComments(comments.filter((c) => c.id !== commentId));
    } catch (error) {
      console.error('Failed to delete comment:', error);
    }
  };

  const getAssigneeName = (userId: string) => {
    const member = organizationMembers.find((m) => m.user_id === userId);
    return (
      member?.full_name ||
      member?.user_name ||
      member?.user_email ||
      'Unassigned'
    );
  };

  const getAreaName = (areaId: string) => {
    const area = areas.find((a) => a.id === areaId);
    return area?.name || '';
  };

  const getOkrObjective = (okrId: string) => {
    const okr = okrs.find((o) => o.id === okrId);
    return okr?.objective || '';
  };

  if (!task) return null;

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-2xl">
                  <div className="flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
                    {/* Header */}
                    <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {task.formatted_reference && (
                              <span className="text-sm font-mono text-gray-500">
                                {task.formatted_reference}
                              </span>
                            )}
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                task.status === 'complete'
                                  ? 'bg-green-100 text-green-800'
                                  : task.status === 'in_progress'
                                    ? 'bg-blue-100 text-blue-800'
                                    : task.status === 'blocked'
                                      ? 'bg-red-100 text-red-800'
                                      : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {formatTaskStatus(task.status)}
                            </span>
                          </div>
                          {editMode ? (
                            <input
                              type="text"
                              value={editForm.title}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  title: e.target.value,
                                })
                              }
                              className="text-lg font-semibold w-full px-2 py-1 border border-gray-300 rounded-md"
                            />
                          ) : (
                            <h2 className="text-lg font-semibold text-gray-900">
                              {task.title}
                            </h2>
                          )}
                        </div>
                        <div className="ml-3 flex items-center gap-2">
                          {canEdit && (
                            <>
                              {editMode ? (
                                <>
                                  <button
                                    onClick={handleSave}
                                    disabled={saving}
                                    className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 disabled:opacity-50"
                                  >
                                    {saving ? 'Saving...' : 'Save'}
                                  </button>
                                  <button
                                    onClick={() => setEditMode(false)}
                                    className="rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <button
                                  onClick={() => setEditMode(true)}
                                  className="rounded-md bg-white p-2 text-gray-400 hover:text-gray-500"
                                >
                                  <PencilIcon className="h-5 w-5" />
                                </button>
                              )}
                              <button
                                onClick={() => {
                                  if (
                                    window.confirm(
                                      'Are you sure you want to delete this task?',
                                    )
                                  ) {
                                    onDelete(task.id);
                                    onClose();
                                  }
                                }}
                                className="rounded-md bg-white p-2 text-gray-400 hover:text-red-500"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </>
                          )}
                          <button
                            onClick={onClose}
                            className="rounded-md bg-white p-2 text-gray-400 hover:text-gray-500"
                          >
                            <XMarkIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 px-6 py-4">
                      {/* Quick Actions Bar */}
                      {editMode && (
                        <div className="mb-6 grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Status
                            </label>
                            <select
                              value={editForm.status}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  status: e.target.value as
                                    | 'not_started'
                                    | 'in_progress'
                                    | 'complete'
                                    | 'blocked',
                                })
                              }
                              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            >
                              <option value="not_started">Not Started</option>
                              <option value="in_progress">In Progress</option>
                              <option value="complete">Complete</option>
                              <option value="blocked">Blocked</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Priority
                            </label>
                            <select
                              value={editForm.priority}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  priority: e.target.value as
                                    | 'high'
                                    | 'medium'
                                    | 'low',
                                })
                              }
                              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            >
                              <option value="high">High</option>
                              <option value="medium">Medium</option>
                              <option value="low">Low</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Assigned To
                            </label>
                            <select
                              value={editForm.assigned_to_id || ''}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  assigned_to_id: e.target.value || undefined,
                                })
                              }
                              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            >
                              <option value="">Unassigned</option>
                              {organizationMembers.map((member) => (
                                <option
                                  key={member.user_id}
                                  value={member.user_id}
                                >
                                  {member.full_name ||
                                    member.user_name ||
                                    member.user_email}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Due Date
                            </label>
                            <input
                              type="date"
                              value={
                                editForm.due_date
                                  ? editForm.due_date.split('T')[0]
                                  : ''
                              }
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  due_date: e.target.value
                                    ? `${e.target.value}T00:00:00`
                                    : undefined,
                                })
                              }
                              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Area
                            </label>
                            <select
                              value={editForm.area_id || ''}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  area_id: e.target.value || undefined,
                                })
                              }
                              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            >
                              <option value="">No area</option>
                              {areas.map((area) => (
                                <option key={area.id} value={area.id}>
                                  {area.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              OKR
                            </label>
                            <select
                              value={editForm.okr_id || ''}
                              onChange={(e) => {
                                const okrId = e.target.value || undefined;
                                setEditForm({
                                  ...editForm,
                                  okr_id: okrId,
                                  key_result_id: undefined,
                                });
                                if (okrId) {
                                  loadKeyResults(okrId);
                                } else {
                                  setKeyResults([]);
                                }
                              }}
                              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            >
                              <option value="">No OKR alignment</option>
                              {okrs.map((okr) => (
                                <option
                                  key={okr.id}
                                  value={okr.id}
                                  disabled={okr.disabled}
                                  className={
                                    okr.disabled ? 'text-gray-400 italic' : ''
                                  }
                                >
                                  {okr.formatted_reference
                                    ? `${okr.formatted_reference} - `
                                    : ''}
                                  {okr.objective}
                                  {okr.displaySuffix || ''}
                                </option>
                              ))}
                            </select>
                          </div>
                          {editForm.okr_id && keyResults.length > 0 && (
                            <div className="col-span-2">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Key Result
                              </label>
                              <select
                                value={editForm.key_result_id || ''}
                                onChange={(e) =>
                                  setEditForm({
                                    ...editForm,
                                    key_result_id: e.target.value || undefined,
                                  })
                                }
                                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                              >
                                <option value="">No specific key result</option>
                                {keyResults.map((kr) => (
                                  <option key={kr.id} value={kr.id}>
                                    {kr.formatted_reference
                                      ? `${kr.formatted_reference} - `
                                      : ''}
                                    {kr.description}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Metadata Display (when not editing) */}
                      {!editMode && (
                        <div className="mb-6 space-y-3">
                          <div className="flex items-center gap-6 text-sm text-gray-600">
                            {task.priority && (
                              <div className="flex items-center gap-1">
                                <FlagIcon
                                  className={`h-4 w-4 ${
                                    task.priority === 'high'
                                      ? 'text-red-500'
                                      : task.priority === 'medium'
                                        ? 'text-yellow-500'
                                        : 'text-gray-400'
                                  }`}
                                />
                                <span>
                                  {task.priority.charAt(0).toUpperCase() +
                                    task.priority.slice(1)}{' '}
                                  Priority
                                </span>
                              </div>
                            )}
                            {task.due_date && (
                              <div className="flex items-center gap-1">
                                <CalendarIcon className="h-4 w-4" />
                                <span>
                                  {format(
                                    new Date(task.due_date),
                                    'MMM d, yyyy',
                                  )}
                                </span>
                              </div>
                            )}
                            {task.assigned_to_id && (
                              <div className="flex items-center gap-1">
                                <UserIcon className="h-4 w-4" />
                                <span>
                                  {getAssigneeName(task.assigned_to_id)}
                                </span>
                              </div>
                            )}
                          </div>
                          {task.okr_id && (
                            <div className="flex items-center gap-1 text-sm text-blue-600">
                              <RocketLaunchIcon className="h-4 w-4" />
                              <span>{getOkrObjective(task.okr_id)}</span>
                              {task.key_result_id &&
                                keyResults.find(
                                  (kr) => kr.id === task.key_result_id,
                                ) && (
                                  <span className="text-gray-500">
                                    •{' '}
                                    {
                                      keyResults.find(
                                        (kr) => kr.id === task.key_result_id,
                                      )?.description
                                    }
                                  </span>
                                )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Description Section */}
                      <div className="mb-6">
                        <h3 className="text-sm font-semibold text-gray-900 mb-2">
                          Description
                        </h3>
                        {editMode ? (
                          <MarkdownEditor
                            value={editForm.description || ''}
                            onChange={(value) =>
                              setEditForm({ ...editForm, description: value })
                            }
                            organizationId={organizationId}
                            placeholder="Add a description..."
                          />
                        ) : (
                          <div className="prose prose-sm max-w-none">
                            {task.description ? (
                              <MarkdownViewer content={task.description} />
                            ) : (
                              <p className="text-gray-500 italic">
                                No description
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Notes Section */}
                      <div className="mb-6">
                        <h3 className="text-sm font-semibold text-gray-900 mb-2">
                          Notes
                        </h3>
                        {editMode ? (
                          <MarkdownEditor
                            value={editForm.notes || ''}
                            onChange={(value) =>
                              setEditForm({ ...editForm, notes: value })
                            }
                            organizationId={organizationId}
                            placeholder="Add notes... (@user, @okr, @company, @segment, @proposition to link)"
                          />
                        ) : (
                          <div className="prose prose-sm max-w-none">
                            {task.notes ? (
                              <MarkdownViewer content={task.notes} />
                            ) : (
                              <p className="text-gray-500 italic">No notes</p>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Comments Section */}
                      <div className="border-t pt-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                            <ChatBubbleLeftIcon className="h-4 w-4" />
                            Comments
                            {comments.length > 0 && (
                              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                                {comments.length}
                              </span>
                            )}
                          </h3>
                        </div>

                        {/* Add Comment */}
                        <div className="mb-4">
                          <MarkdownEditor
                            value={newComment}
                            onChange={setNewComment}
                            organizationId={organizationId}
                            placeholder="Add a comment... (@user, @okr, @company, @segment, @proposition to mention)"
                            disabled={savingComment}
                          />
                          <div className="mt-2 flex justify-end">
                            <button
                              onClick={handleAddComment}
                              disabled={!newComment.trim() || savingComment}
                              className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {savingComment ? 'Posting...' : 'Post Comment'}
                            </button>
                          </div>
                        </div>

                        {/* Comments List */}
                        {loadingComments ? (
                          <div className="text-center py-4">
                            <div className="inline-flex items-center text-sm text-gray-500">
                              Loading comments...
                            </div>
                          </div>
                        ) : comments.length === 0 ? (
                          <div className="text-center py-8 text-gray-500 text-sm">
                            No comments yet. Be the first to comment!
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {comments.map((comment) => (
                              <div
                                key={comment.id}
                                className="bg-gray-50 rounded-lg p-4"
                              >
                                <div className="flex items-start justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center text-sm font-medium text-white">
                                      {comment.user?.first_name?.[0] ||
                                        comment.user?.email[0] ||
                                        '?'}
                                    </div>
                                    <div>
                                      <div className="text-sm font-medium text-gray-900">
                                        {comment.user?.full_name ||
                                          comment.user?.email ||
                                          'Unknown'}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        {formatDistanceToNow(
                                          new Date(comment.created_at),
                                          { addSuffix: true },
                                        )}
                                        {comment.updated_at !==
                                          comment.created_at && ' (edited)'}
                                      </div>
                                    </div>
                                  </div>
                                  {(comment.user_id === currentUserId ||
                                    isAdmin) && (
                                    <div className="flex items-center gap-1">
                                      {editingCommentId === comment.id ? (
                                        <>
                                          <button
                                            onClick={() =>
                                              handleEditComment(comment.id)
                                            }
                                            className="p-1 text-gray-400 hover:text-green-600"
                                          >
                                            <CheckIcon className="h-4 w-4" />
                                          </button>
                                          <button
                                            onClick={() => {
                                              setEditingCommentId(null);
                                              setEditingCommentContent('');
                                            }}
                                            className="p-1 text-gray-400 hover:text-gray-600"
                                          >
                                            <XMarkIcon className="h-4 w-4" />
                                          </button>
                                        </>
                                      ) : (
                                        <>
                                          <button
                                            onClick={() => {
                                              setEditingCommentId(comment.id);
                                              setEditingCommentContent(
                                                comment.content,
                                              );
                                            }}
                                            className="p-1 text-gray-400 hover:text-blue-600"
                                          >
                                            <PencilIcon className="h-4 w-4" />
                                          </button>
                                          <button
                                            onClick={() => {
                                              if (
                                                window.confirm(
                                                  'Delete this comment?',
                                                )
                                              ) {
                                                handleDeleteComment(comment.id);
                                              }
                                            }}
                                            className="p-1 text-gray-400 hover:text-red-600"
                                          >
                                            <TrashIcon className="h-4 w-4" />
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  )}
                                </div>
                                {editingCommentId === comment.id ? (
                                  <MarkdownEditor
                                    value={editingCommentContent}
                                    onChange={setEditingCommentContent}
                                    organizationId={organizationId}
                                    placeholder="Edit comment... (@user, @okr, @company, @segment, @proposition to mention)"
                                  />
                                ) : (
                                  <div className="prose prose-sm max-w-none">
                                    <MarkdownViewer content={comment.content} />
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default TaskDetailPanel;
